import { useState, useEffect, type ReactNode } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Users, Calendar, FileText, FileSignature, Wallet,
  Building2, HardHat, BarChart3, MessageCircle, Sparkles, Settings, LogOut,
  Menu, X, ListChecks, GraduationCap, Shield, UserCheck, Upload, UserPlus,
  UtensilsCrossed,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useTenant } from '@/hooks/useTenant'
import { useVocab, useModulesVisible, usePrimaryColor } from '@/hooks/useVocab'
import { useSystemBranding } from '@/hooks/useSystemBranding'
import { clearImpersonation, getImpersonationName } from '@/modules/admin/impersonation'
import { BackupButton } from '@/modules/backup/BackupButton'
import ThemeToggle from '@/components/ThemeToggle'
import { useWatermark } from '@/hooks/useWatermark'

interface Props { children: ReactNode }

// Cada item conhece sua "chave de módulo" (clients, events, ...) que vai
// bater com o que o tenant tem em modules_visible.
const NAV_BUILDER = (v: ReturnType<typeof useVocab>) => [
  { key: 'dashboard',   to: '/dashboard',     icon: LayoutDashboard, label: 'Dashboard' },
  { key: 'clients',     to: '/clientes',      icon: Users,           label: v.clients },
  { key: 'events',      to: '/eventos',       icon: Calendar,        label: v.events },
  { key: 'proposals',   to: '/propostas',     icon: FileText,        label: v.proposals },
  { key: 'contracts',   to: '/contratos',     icon: FileSignature,   label: v.contracts },
  { key: 'finance',     to: '/financeiro',    icon: Wallet,          label: 'Financeiro' },
  { key: 'suppliers',   to: '/fornecedores',  icon: Building2,       label: v.suppliers },
  { key: 'buffet',      to: '/buffet',        icon: UtensilsCrossed, label: 'Buffet' },
  { key: 'team',        to: '/equipe',        icon: HardHat,         label: v.team },
  { key: 'schedule',    to: '/cronograma',    icon: ListChecks,      label: v.schedule },
  { key: 'guest_list',  to: '/lista-presenca', icon: UserCheck,      label: 'Lista de presença' },
  { key: 'reports',     to: '/relatorios',    icon: BarChart3,       label: 'Relatórios' },
  { key: 'whatsapp',    to: '/whatsapp',      icon: MessageCircle,   label: 'WhatsApp' },
  { key: 'leads',       to: '/leads',         icon: Sparkles,        label: 'Leads' },
  { key: 'formaturas',  to: '/formaturas',    icon: GraduationCap,   label: 'Formaturas' },
  { key: 'support',     to: '/suporte',       icon: MessageCircle,   label: 'Suporte' },
  { key: 'settings',    to: '/configuracoes', icon: Settings,        label: 'Configurações' },
]

export function AppLayout({ children }: Props) {
  const { user, signOut } = useAuth()
  const { tenant, isTrialing, isSuspended } = useTenant()
  const vocab = useVocab()
  const modulesVisible = useModulesVisible()
  const primaryColor = usePrimaryColor()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  const onLogout = async () => {
    await signOut()
    navigate('/login')
  }

  // Lê o logo customizado do tenant (white-label) e o nome de exibição.
  // Cascade: logo do tenant > logo do sistema > emoji padrão.
  const tenantData = tenant as unknown as {
    logo_url?: string
    profession_meta?: { nome?: string }
    watermark_enabled?: boolean
    watermark_opacity?: number
    watermark_size?: number
    watermark_gap?: number
    watermark_angle?: number
  }
  const { data: branding } = useSystemBranding()
  const logoUrl = tenantData?.logo_url || branding?.logo_url
  // Marca d'água do logo no fundo (opcional, configurada em Aparência).
  const watermarkOn = !!tenantData?.watermark_enabled && !!logoUrl
  const watermarkOpacity = typeof tenantData?.watermark_opacity === 'number' ? tenantData.watermark_opacity : 0.09
  // `watermark_size` era o tamanho do logo único em vw; agora é o lado do logo
  // DENTRO do padrão, em px. Os valores antigos (55 = "55vw") não fazem sentido
  // na medida nova, então qualquer coisa abaixo de 60 volta para o padrão.
  const tamanhoSalvo = tenantData?.watermark_size
  const watermarkLogoPx = typeof tamanhoSalvo === 'number' && tamanhoSalvo >= 60 ? tamanhoSalvo : 150
  const watermarkGapPx = typeof tenantData?.watermark_gap === 'number' ? tenantData.watermark_gap : 90
  const watermarkAngle = typeof tenantData?.watermark_angle === 'number' ? tenantData.watermark_angle : -24
  const watermarkMask = useWatermark(watermarkOn ? logoUrl : undefined, {
    logoPx: watermarkLogoPx,
    gapPx: watermarkGapPx,
  })
  // Nome principal grande: NOME DA EMPRESA (nome_fantasia)
  // Subtítulo pequeno: tipo do sistema/perfil (profession_meta.nome)
  const empresaNome = tenant?.nome_fantasia || branding?.nome_sistema || 'EventPro'
  const sistemaTipo = tenantData?.profession_meta?.nome || ''

  // Atualiza title da aba e favicon dinâmicos com a marca do sistema
  useEffect(() => {
    const systemName = branding?.nome_sistema || 'EventPro Manager'
    document.title = empresaNome !== systemName
      ? `${empresaNome} — ${systemName}`
      : systemName
    if (branding?.logo_url) {
      let link = document.querySelector("link[rel='icon']") as HTMLLinkElement | null
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.appendChild(link)
      }
      link.href = branding.logo_url
    }
  }, [empresaNome, branding?.nome_sistema, branding?.logo_url])

  // Constrói o menu com vocabulário traduzido e filtra pelo que o tenant tem ativo.
  // "Configurações" é sempre visível: é área essencial de conta/segurança (trocar
  // senha/e-mail, LGPD) e, pro master sem tenant, é o único caminho até a aba Segurança.
  const NAV = NAV_BUILDER(vocab)
  const visibleNav = NAV.filter(item => item.key === 'settings' || modulesVisible.includes(item.key))

  // Painel Master só aparece pro master, independente de modules_visible
  const isMaster = user?.role === 'master'

  return (
    <div className="min-h-screen flex bg-bg" style={{ ['--primary' as string]: primaryColor }}>
      {/* Marca d'água do logo no fundo — silhueta recolorida pelo tema (claro/escuro) */}
      {watermarkOn && watermarkMask && (
        <div
          className="app-watermark"
          aria-hidden="true"
          style={{
            WebkitMaskImage: `url("${watermarkMask}")`,
            maskImage: `url("${watermarkMask}")`,
            // A célula já contém o logo mais o respiro em volta.
            WebkitMaskSize: `${watermarkLogoPx + watermarkGapPx}px`,
            maskSize: `${watermarkLogoPx + watermarkGapPx}px`,
            opacity: watermarkOpacity,
            ['--watermark-angle' as string]: `${watermarkAngle}deg`,
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                    fixed lg:static z-40 inset-y-0 left-0 w-64
                    bg-card border-r border-border flex flex-col
                    transition-transform`}
      >
        <div className="p-5 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-2">
            {/* Logo do tenant (white-label) > logo do sistema > logo padrão EventPro */}
            <img
              src={logoUrl || '/logo-eventpro-icone.png'}
              alt={empresaNome}
              className="w-10 h-10 rounded-lg object-cover"
            />
            <div className="min-w-0">
              {/* Empresa em cima (grande) */}
              <div className="font-extrabold text-base leading-tight truncate" title={empresaNome}>
                {empresaNome}
              </div>
              {/* Tipo do sistema embaixo (pequeno) */}
              {sistemaTipo && (
                <div className="text-[10px] text-text-secondary truncate uppercase tracking-wide font-bold max-w-[160px]" title={sistemaTipo}>
                  {sistemaTipo}
                </div>
              )}
            </div>
          </Link>
        </div>

        <nav className="flex-1 overflow-auto p-3 space-y-1">
          {visibleNav.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition
                 ${isActive
                    ? 'text-text-primary'
                    : 'text-text-secondary hover:bg-card-hover hover:text-text-primary'}`
              }
              style={({ isActive }) => isActive
                ? { background: `${primaryColor}22`, color: primaryColor }
                : undefined
              }
              onClick={() => setOpen(false)}
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}

          {/* Importar — visível pra master e pra tenant_owner (dono do tenant) */}
          {(isMaster || user?.role === 'tenant_owner') && (
            <>
              <div className="border-t border-border my-2" />
              <NavLink
                to="/importar"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition
                   ${isActive
                      ? 'bg-accent/20 text-accent'
                      : 'text-text-secondary hover:bg-card-hover hover:text-text-primary'}`
                }
                onClick={() => setOpen(false)}
              >
                <Upload size={18} />
                Importar
              </NavLink>
            </>
          )}

          {/* Painel Master — só visível pro master */}
          {isMaster && (
            <>
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition
                   ${isActive
                      ? 'bg-accent/20 text-accent'
                      : 'text-text-secondary hover:bg-card-hover hover:text-text-primary'}`
                }
                onClick={() => setOpen(false)}
              >
                <Shield size={18} />
                Painel Master
              </NavLink>
              <NavLink
                to="/admin/tenants?novo=1"
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition
                           text-text-secondary hover:bg-card-hover hover:text-text-primary"
                onClick={() => setOpen(false)}
              >
                <UserPlus size={18} />
                Novo cliente
              </NavLink>
            </>
          )}
        </nav>

        <div className="p-3 border-t border-border space-y-2">
          <div className="text-xs px-2 py-1 text-text-secondary">
            {user?.name || user?.email}
          </div>
          <div className="px-2 py-1">
            <ThemeToggle showLabel className="w-full justify-center" />
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                       text-text-secondary hover:bg-danger/10 hover:text-danger transition"
          >
            <LogOut size={16} /> Sair
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 relative z-[1]">
        {/* Topbar mobile */}
        <header className="lg:hidden p-3 border-b border-border bg-card flex items-center gap-3">
          <button onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
          <div className="font-bold">EventPro</div>
        </header>

        {/* Banner de impersonação — master tá vendo/operando como cliente.
            Tem super-poderes: pode editar tudo (vai pelas regras Firestore via isMaster())
            e pode baixar backup completo direto daqui pra ter cópia segura antes de mexer. */}
        {user?.impersonating && tenant && (
          <div className="bg-warning/15 border-b border-warning/40 text-warning text-xs px-4 py-2
                          flex items-center justify-center gap-3 flex-wrap">
            <Shield size={14} />
            <span>
              <strong>Modo super-admin</strong> · operando como{' '}
              <strong>{getImpersonationName() || tenant.nome_fantasia || 'cliente'}</strong>
              {' '}· você pode editar/apagar/criar qualquer coisa
            </span>
            <span className="hidden sm:inline">
              <BackupButton tenant={{ id: tenant.id, nome_fantasia: tenant.nome_fantasia }} />
            </span>
            <button
              onClick={() => { clearImpersonation(); window.location.assign('/admin/tenants') }}
              className="font-bold underline hover:no-underline"
            >
              Sair do modo cliente
            </button>
          </div>
        )}

        {/* Banners */}
        {isTrialing && (
          <div className="bg-warning/10 border-b border-warning text-warning text-sm px-4 py-2 text-center">
            Você está no período de teste. Aproveite todas as funcionalidades por mais alguns dias.
          </div>
        )}
        {isSuspended && (
          <div className="bg-danger/10 border-b border-danger text-danger text-sm px-4 py-2 text-center">
            Seu plano está suspenso. <Link to="/configuracoes/cobranca" className="underline">Renovar agora</Link>
          </div>
        )}

        <main className="flex-1 overflow-auto p-4 lg:p-6">{children}</main>
      </div>
    </div>
  )
}
